import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Input,
  Spinner,
  Stack,
  Switch,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import * as Yup from 'yup';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ICPFValidate, useDocumentValidation } from '../../../../services/hooks/Registration/useDocumentValidation';
import { validateCPF } from '../../../../utils/validateCPF';
import inputMask from 'react-input-mask';
import { AxiosError } from 'axios';
import { ETypeExemption } from '../../../../services/hooks/SelectiveProcess/Types';
import { useContext } from 'react';
import { ContextAuth } from '../../../../contexts/Authentication';
import ValidateCpfMobilePage from './mobile';
import { useRequestDetailSelectiveProcess } from '../../../../services/hooks/SelectiveProcess/useRequestDetailSelectiveProcess';
import { FaUserCheck } from 'react-icons/fa';
import Header from '../../../../components/Header/Header';

const SelectiveProcessValidationCPF: React.FC = () => {
  const selectiveProcessId = useLocation().pathname.split('/')[2];
  const [searchParams] = useSearchParams();
  const spData = useRequestDetailSelectiveProcess(selectiveProcessId);
  const isExemption: boolean = searchParams?.get('isExemption') == 'true';
  const impugnation: boolean = searchParams?.get('impugnation') == 'true';

  const exemption_registration_creation_type = searchParams?.get('exemption_registration_creation_type');

  const navigate = useNavigate();
  const { isMobile } = useContext(ContextAuth);
  const { mutateAsync, isPending } = useDocumentValidation();
  const toast = useToast();

  const schema = Yup.object().shape({
    is_foreign: Yup.boolean(),
    document_number: Yup.string().when({
      is: () => verifyIsForeign(),
      then: (schema) => schema.required('Campo obrigatório'),
      otherwise: (schema) =>
        schema
          .required('Campo obrigatório')
          .test('invalidCPF', 'CPF incompleto', (value) => {
            const cpf = value ?? '';
            const digits = cpf.replace(/\D/g, '');
            return digits.length >= 11;
          })
          .test('invalidCPF', 'Informe um CPF válido', (value) => {
            const cpf = value ?? '';
            const numbers = cpf.replace(/\D/g, '');
            if (numbers.length === 11) return validateCPF(numbers);
            else if (numbers.length > 11) return false;
            return true;
          }),
    }),
  });

  const hookforms = useForm({
    resolver: yupResolver(schema),
  });

  const verifyIsForeign = () => {
    return hookforms.watch('is_foreign');
  };

  const cpfVerify = (payload: FieldValues) => {
    mutateAsync(payload)
      .then(() => {
        toast({
          title: 'ATENÇÃO',
          description:
            'Verificamos que você já possui cadastro em nosso sistema. Realize seu login através do seu e-mail e senha.',
          status: 'info',
          duration: 10000,
          isClosable: true,
        });
        if (isExemption) {
          if (exemption_registration_creation_type === ETypeExemption.standby) {
            navigate(
              `/login?selective_process_id=${selectiveProcessId}&route=/processo-seletivo/${selectiveProcessId}/isencao`,
            );
            return;
          } else {
            navigate(
              `/login?selective_process_id=${selectiveProcessId}&route=/processo-seletivo/${selectiveProcessId}/inscricao/isencao`,
            );
            return;
          }
        }
        if (impugnation) {
          navigate(
            `/login?selective_process_id=${selectiveProcessId}&route=${isMobile ? `/painel/impugnacao/${selectiveProcessId}?om=true` : `/painel/${selectiveProcessId}/impugnacao`}`,
          );
        } else {
          navigate(
            `/login?selective_process_id=${selectiveProcessId}&route=/processo-seletivo/${selectiveProcessId}/inscricao`,
          );
        }
      })
      .catch((e: AxiosError<ICPFValidate>) => {
        toast({
          title: 'Realize seu cadastro',
          description: e.response?.data?.message || 'Tente novamente mais tarde',
          status: 'info',
          duration: 4000,
          isClosable: true,
        });
        if (isExemption) {
          if (exemption_registration_creation_type === ETypeExemption.standby) {
            sessionStorage.setItem('document_number', payload?.document_number);
            navigate(
              `/cadastro?selective_process_id=${selectiveProcessId}&is_foreign=${payload.is_foreign}&route=/processo-seletivo/${selectiveProcessId}/isencao`,
            );
            return;
          } else {
            sessionStorage.setItem('document_number', payload?.document_number);
            navigate(
              `/cadastro?selective_process_id=${selectiveProcessId}&is_foreign=${payload.is_foreign}&route=/processo-seletivo/${selectiveProcessId}/inscricao/isencao`,
            );
            return;
          }
        }
        if (impugnation) {
          sessionStorage.setItem('document_number', payload?.document_number);
          navigate(
            `/cadastro?selective_process_id=${selectiveProcessId}&is_foreign=${payload.is_foreign}&route=${isMobile ? `/painel/impugnacao/${selectiveProcessId}?om=true` : `/painel/${selectiveProcessId}/impugnacao`}`,
          );
        } else {
          sessionStorage.setItem('document_number', payload?.document_number);
          navigate(
            `/cadastro?selective_process_id=${selectiveProcessId}&is_foreign=${payload.is_foreign}&route=/processo-seletivo/${selectiveProcessId}/inscricao`,
          );
        }
      });
  };

  const isForeigner = hookforms.watch('is_foreign') ?? false;

  if (isMobile)
    return (
      <FormProvider {...hookforms}>
        <ValidateCpfMobilePage onCpfVerify={cpfVerify} isLoading={isPending} />
      </FormProvider>
    );

  return (
    <Box h="100dvh" bgColor="#f5f5f5">
      <Header />
      <Divider />
      <Container maxW="container.md" h="calc(100% - 100px)" centerContent>
        <VStack w="100%" h="100%" justify="center">
          <Card w="100%" bg="white" borderRadius="xl" boxShadow="xl" overflow="hidden">
            <CardHeader bg={isForeigner ? 'blue.700' : 'blue.500'} color="white" borderTopRadius="xl">
              <HStack align="center">
                <Icon as={FaUserCheck} w={6} h={6} mr={3} ml={2} />
                <Stack>
                  <Heading size="md">FORMULÁRIO DE INSCRIÇÃO</Heading>
                  <Heading fontWeight="normal" size="xs">
                    {spData.data?.name ?? '--'}
                  </Heading>
                </Stack>
              </HStack>
            </CardHeader>

            <CardBody>
              <form onSubmit={hookforms.handleSubmit(cpfVerify)}>
                <VStack spacing={3} align="stretch">
                  {spData?.data?.allows_foreigners && (
                    <FormControl my={3}>
                      <HStack alignItems="center">
                        <Switch
                          {...hookforms.register('is_foreign', {
                            onChange: () => {
                              hookforms.setValue('document_number', '');
                            },
                          })}
                          size="lg"
                          colorScheme="blue"
                          isChecked={isForeigner}
                        />
                        <FormLabel fontWeight="semibold" m={0} color="gray.700">
                          Sou estrangeiro não naturalizado
                        </FormLabel>
                      </HStack>
                    </FormControl>
                  )}

                  <FormControl isRequired isInvalid={!!hookforms.formState.errors.document_number?.message}>
                    <FormLabel fontWeight="semibold" color="gray.700">
                      {isForeigner ? 'Número do Passaporte' : 'CPF'}
                    </FormLabel>
                    <Input
                      type="text"
                      placeholder={isForeigner ? 'Digite o número do passaporte' : 'Digite o número do CPF'}
                      size="md"
                      fontSize="md"
                      {...hookforms.register('document_number')}
                      borderColor={isForeigner ? 'blue.500' : 'blue.300'}
                      focusBorderColor={isForeigner ? 'blue.700' : 'blue.500'}
                      borderRadius="md"
                      required={false}
                      textTransform={'uppercase'}
                      onInput={
                        isForeigner
                          ? (e) => {
                              e.currentTarget.value = e.currentTarget.value.replace(/[^0-9A-Za-z]/g, '');
                            }
                          : undefined
                      }
                      {...(!isForeigner ? { as: inputMask, mask: '999.999.999-99' } : undefined)}
                      py={6}
                      _placeholder={{ color: 'gray.400' }}
                    />
                    <FormErrorMessage>{hookforms.formState.errors.document_number?.message}</FormErrorMessage>
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      {!isForeigner ? 'Formato: 000.000.000-00' : 'Formato: 00000000000'}
                    </Text>
                  </FormControl>
                  <Divider />
                  <Button
                    type="submit"
                    bgColor={isForeigner ? 'blue.700' : 'blue.500'}
                    color="white"
                    size="lg"
                    height="45px"
                    borderRadius="lg"
                    fontSize="lg"
                    isLoading={isPending}
                    loadingText="Verificando documento..."
                    spinnerPlacement="start"
                    leftIcon={isPending ? <Spinner size="sm" /> : undefined}
                    w="100%"
                    _hover={{
                      transform: 'translateY(-2px)',
                      boxShadow: 'lg',
                    }}
                    transition="all 0.2s"
                    boxShadow="md"
                  >
                    Continuar
                  </Button>
                </VStack>
              </form>
            </CardBody>
          </Card>
        </VStack>
      </Container>
    </Box>
  );
};

export default SelectiveProcessValidationCPF;
